import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  catchError,
  combineLatest,
  finalize,
  first,
  map,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import {environment} from '../../../environments/environment';
import {FormReturn} from '../../../libs/az-forms/interfaces/api-object-formatted';
import {MarkerData} from '../../../libs/az-geolocator/interfaces/markers';
import {ApiCallErrorMessages} from '../../enums/api-call-error-messages';
import {Endpoints, GeoservicesEndpoints} from '../../enums/endpoints.enum';
import {FieldsUuidEnum} from '../../enums/fields-uuid.enum';
import {
  AddressFormNames,
  AddressFormUuids,
  ShippingAddressFormUuids,
} from '../../enums/form-names.enum';
import {RoutingEnum} from '../../enums/routing.enum';
import {SessionStorageNames} from '../../enums/session-storage-names.enum';
import {
  MotorQuoteDTO,
  MotorQuoteInputDTO,
  MotorQuoteWarrantyDTO,
} from '../../models/MotorQuoteDTO.interfaces';
import {HomepageForm} from '../../models/homepage-form';
import {UpdateAddressDTO} from '../../models/update-address';
import {fakeAgency} from '../../static/get-acency-mock';
import {ApiUrlService} from '../apiUrl/api-url.service';
import {HttpService} from '../http/http.service';
import {LoaderService} from '../loader/loader.service';
import {RouterDataService} from '../router-data/router-data.service';
import {SessionStorageService} from '../session-storage/session-storage.service';
import {AnalyticsService} from './../analytics/analytics.service';
import {ContactsMapService} from './../contacts-map/contacts-map.service';
import {DateHelperService} from './../date-helper/date-helper.service';
import {PageTypeService} from '../page-type/page-type.service';
import {MotorReferral, MotorStatus, MotorStatusMessages} from '../../enums/motorStatus.enum';
import {LeadSavePayload} from '../../models/lead.model';
import {LeadService} from '../lead/lead.service';
import {MotorQuoteConfigurations} from '../../enums/motor-quote-configurations';
import {NxDialogService} from '@aposin/ng-aquila/modal';
import {ModalAlertComponent} from '../../components/modals/modal-alert/modal-alert.component';
import {GeoservicesUrlService} from '../geoservices-url/geoservices-url.service';
import {PrivacyService} from '../privacy/privacy.service';

@Injectable({
  providedIn: 'root',
})
export class FastquoteService {
  private isMoreThan60days!: boolean;
  private paymentRetry$?: BehaviorSubject<boolean>;
  private initialFormData!: HomepageForm;
  private _isEcommerceActiveForAgency!: boolean;
  constructor(
    private router: Router,
    private apiUrlService: ApiUrlService,
    private geoservicesUrlService: GeoservicesUrlService,
    private httpService: HttpService,
    private routerDataService: RouterDataService,
    private sessionStorageService: SessionStorageService,
    private analyticsService: AnalyticsService,
    private dateHelperService: DateHelperService,
    private contactsMapService: ContactsMapService,
    private loaderService: LoaderService,
    private pageTypeService: PageTypeService,
    private leadService: LeadService,
    private dialogService: NxDialogService,
    private privacyService: PrivacyService,
  ) {}

  setHomepageFormData(val: HomepageForm) {
    this.initialFormData = val;
  }
  getHomepageFormData() {
    return this.initialFormData;
  }

  setPaymentRetry(val: boolean) {
    this.paymentRetry$ = new BehaviorSubject(val);
  }
  getPaymentRetry() {
    const ret = this.paymentRetry$?.asObservable().pipe(take(1));
    return ret;
  }

  checkApiCallStatus(data: MotorQuoteDTO, step?: string): Observable<MotorQuoteDTO | never> {
    if (data.status === MotorStatus.OK || data.status === MotorStatus.BUY_ONLY) {
      return of(data);
    }
    //this.routerDataService.setMotorQuoteObject(data);
    if (
      data.message === ApiCallErrorMessages.ALREADY_INSURED_OVER ||
      data.message === ApiCallErrorMessages.ALREADY_INSURED_UNDER
    ) {
      this.router.navigate(['/', RoutingEnum.AlreadyInsured], {skipLocationChange: true});
      return EMPTY;
    }

    this.router.navigate(['/', RoutingEnum.QuotationError], {
      skipLocationChange: true,
      state: {error: data, step: step},
    });
    return EMPTY;
  }

  manageData(obj: Record<string, unknown>) {
    const conversionValues: Record<string, string> = {
      p: 'vehiclePlate',
      b: 'insuredBirthday',
      e: 'insuredEmail',
      a: 'agency',
      r: 'referral',
      py: 'privacy',
    };
    const initData = Object.keys(obj).reduce((retObj: Record<string, unknown>, el) => {
      const newKey = conversionValues[el] ?? null;
      if (newKey) {
        retObj[newKey] = obj[el];
      }
      return retObj;
    }, {});
    return initData;
  }

  initData(response: FormReturn, sendAnalytics: boolean = true) {
    const queryParams: Record<string, unknown> = this.router.parseUrl(this.router.url).queryParams;
    if (queryParams?.['a']) {
      response.data.agency = queryParams['a'];
      response.data.referral = MotorReferral.AGENCY;
    }
    response.data.vehiclePlate = response.data.vehiclePlate.toUpperCase();
    this.analyticsService.startQuotation(response.data, sendAnalytics);

    const initUrl = this.apiUrlService.getUrl(Endpoints.INIT);

    // const mock = this.httpService.get('./assets/mock/init2024-11-08.json');
    const url = this.httpService.post<MotorQuoteDTO>(initUrl, response.data);
    return url.pipe(
      take(1),
      catchError((error) => {
        this.router.navigate([RoutingEnum.QuotationError], {
          skipLocationChange: true,
          state: {error, step: 'step1'},
        });
        return EMPTY;
      }),
      switchMap((data: MotorQuoteDTO) => {
        if (data?.response?.configuration?.agency?.cod) {
          return this.getAndSetAgency(data);
        }
        return of(data);
      }),
      switchMap((data) => this.checkApiCallStatus(data, 'step1')),
      tap((data) => {
        const isDateMoreThan60Days = this.dateHelperService.checkDateFromObject(data);
        this.isMoreThan60days = isDateMoreThan60Days;
        this.routerDataService.setMotorQuoteObject(data);
        this.pageTypeService.overridePageTypeFromMotor(data);
        this.sessionStorageService.set(SessionStorageNames.INIT, data);
      }),
    );
  }

  getConfiguration() {
    const url = this.apiUrlService.getUrl(Endpoints.GET_CONFIGURATION);

    const savedData = this.sessionStorageService.get('fq-dati-form-calcola-targa');
    if(savedData) this.analyticsService.startQuotation(savedData, false);

    return this.httpService.get(url).pipe(
      take(1),
      tap((data) => {
        if (!(data?.status === MotorStatus.OK || data?.status === MotorStatus.BUY_ONLY)) throw data;
      }),
      catchError((error) => {
        this.router.navigate([RoutingEnum.QuotationError], {
          skipLocationChange: true,
          state: {error, step: 'step1'},
        });
        return EMPTY;
        // console.error('init call Error', err);
        // this.router.navigate(['/', RoutingEnum.QuotationError], {skipLocationChange: true});
        // return EMPTY;
      }),
      switchMap((data: MotorQuoteDTO) => {
        if (data?.response?.configuration?.agency?.cod) {
          return this.getAndSetAgency(data, true);
        }
        return of(data);
      }),
      tap((res) => {
        this.analyticsService.startRecoverConfiguration(res);
        this.routerDataService.setMotorQuoteObject(res);}),
    );
  }

  resetConfiguration() {
    const url = this.apiUrlService.getUrl(Endpoints.RESET_CONFIGURATION);

    this.analyticsService.sendAnalytics('Reset response');
    return this.httpService.get(url).pipe(
      take(1),
      tap((res) => {
        this.analyticsService.cleanDigitalDataFromQuotation();
        this.routerDataService.setMotorQuoteObject(res)

        this.routerDataService.setThankYouFlow(undefined);
        this.analyticsService.setOverrideFunnelType('fastquote', true);
        this.privacyService.setSaveFlow(false);
        this.sessionStorageService.clear();
      }),
    );
  }

  setAgency(agency: MarkerData, updateData = true) {
    const setAgencyUrl = this.apiUrlService.getUrl(Endpoints.SET_AGENCY);
    return this.httpService.post<MotorQuoteDTO>(setAgencyUrl, {agency}).pipe(
      tap((data) => {
        if (updateData) {
          const isDateMoreThan60Days = this.dateHelperService.checkDateFromObject(data);
          this.isMoreThan60days = isDateMoreThan60Days;
          this.privacyService.setPrivacyUser(data);
          this.routerDataService.setMotorQuoteObject(data);
        }
      }),
    );
  }

  getAndSetAgency(data: MotorQuoteDTO, skipSetAgency = false) {
    const agencyCode = data.response.configuration.agency.cod;
    const isAlreadyInsured =
      data.message === ApiCallErrorMessages.ALREADY_INSURED_OVER ||
      data.message === ApiCallErrorMessages.ALREADY_INSURED_UNDER;

    const isError = data.status === MotorStatus.ERROR;
    const isWarning = data.status === MotorStatus.WARNING;

    return this.contactsMapService.getAgencyByCod(agencyCode ?? '').pipe(
      catchError((err) => {
        console.error('Agency not found, or something went wrong: ', err);
        this._isEcommerceActiveForAgency = false;
        return of({response: [], status: 'ko'});
      }),
      switchMap((el) => {
        //THIS is the case where ecommerce is notactive for given agency
        this._isEcommerceActiveForAgency = !(el.response.length <= 0);

        this.analyticsService.setEcommerceActiveForAgency(this._isEcommerceActiveForAgency);
        if (!skipSetAgency && !isAlreadyInsured && el.response.length > 0 && !isError && !isWarning) {
          return this.setAgency(el.response[0]);
        }
        if (isAlreadyInsured && el.response.length > 0) {
          data.response.configuration.agency = el.response[0];
          this.routerDataService.setMotorQuoteObject(data);
        }
        return of(data);
      }),
    );
  }

  updateWarranty(warranty: MotorQuoteWarrantyDTO, operationValue: 'ON' | 'OFF') {
    const url =
      this.apiUrlService.getUrl(Endpoints.WARRANTY_SELECTION) +
      '/' +
      warranty.uuid +
      '?operation=' +
      operationValue;

    return this.httpService.post(url, {}).pipe(
      tap((data) => {
        this.routerDataService.setMotorQuoteObject(data);
      }),
      finalize(() =>
        this.analyticsService.sendAnalytics({
          componente: 'personalizza',
          cta: operationValue === 'ON' ? 'aggiungi_modulo' : 'rimuovi_modulo',
          modulo: this.analyticsService.textForAnalytics(warranty.name),
          id_modulo: warranty.uuid,
          prezzo_modulo: warranty.price.toString(),
          evidenza_modulo: warranty.recommended ? 'vero' : 'falso',
          hit_type: 'event',
          // funnel_type: 'fastquote',
        }),
      ),
    );
  }

  changeField(value: string | Record<string, boolean>, type: 'warning' | 'selectChildWarranty') {
    if (typeof value === 'string') {
    }
    const urlParams = new URLSearchParams(typeof value === 'string' ? {value, type} : {type});

    const url = `${this.apiUrlService.getUrl(Endpoints.CHANGE_FIELD)}?${urlParams.toString()}`;

    return this.httpService.post(url, {}).pipe(
      tap((data) => {
        this.routerDataService.setMotorQuoteObject(data);
      }),
      finalize(() =>
        this.analyticsService.sendAnalytics(
          'toggle warranty POPOVER ' + value + ' valore inviato: ' + type,
        ),
      ),
    );
  }

  getFieldOptions(fieldId: string) {
    const urlParams = new URLSearchParams({fieldId});
    const url = `${this.apiUrlService.getUrl(Endpoints.GET_FIELD_OPTIONS)}?${urlParams.toString()}`;
    return this.httpService.post(url, {});
  }

  //NEXT APRROACH IS A FUTURE CR
  /*   updateFields(changedFields: {key: string; value: unknown}[], warranty: MotorQuoteWarrantyDTO): Observable<MotorQuoteDTO> {
    const url = this.apiUrlService.getUrl(Endpoints.UPDATE_FIELD);
    const arrCalls = changedFields.filter((el)=>el.value).map((el) => {
      const data = {
        warrantyId: warranty.uuid,
        fieldId: el.key,
        value: el.value,
      };
      this.analyticsService.sendAnalytics('toggle warranty ' + warranty.uuid + '(' + el.key + ') valore inviato: ' + el.value);
      return this.httpService.post(url, data);
    });
    let index = 0;
    const responses: MotorQuoteDTO[] = [];
    return merge(...arrCalls).pipe(
      catchError(() => {
        index++;
        //TODO ERROR HANDLING
        return EMPTY;
      }),
      tap((data) => {
        index++;
        responses.push(data);
      }),
      filter(() => index === arrCalls.length),
      map(() => responses[responses.length - 1]),
      tap((res) => this.routerDataService.setMotorQuoteObject(res)),
    );
  } */
  updateFields(
    changedFields: {key: string; value: unknown}[],
    warranty: MotorQuoteWarrantyDTO,
  ): Observable<MotorQuoteDTO> {
    const url = this.apiUrlService.getUrl(Endpoints.UPDATE_FIELD);
    const arrCalls = changedFields
      .filter((el) => el.value)
      .map((el) => {
        const data = {
          warrantyId: warranty.uuid,
          fieldId: el.key,
          value: el.value,
        };
        const field = warranty.fields?.find((d) => el.key === d.uuid);
        if (field) {
          const changedValue =
            field.widget?.options && field.widget?.options.length > 0
              ? field.widget.options?.find((res) => res.value === el.value)?.label
              : el.value;
          this.analyticsService.sendAnalytics({
            componente: 'personalizza',
            cta: 'cambia_parametri',
            modulo: this.analyticsService.textForAnalytics(field.label),
            voce_cambiata: `${this.analyticsService.textForAnalytics(field.label)}:${this.analyticsService.textForAnalytics(<string>changedValue ?? '')}`,
            hit_type: 'event',
            // funnel_type: 'fastquote',
          });
        }
        return this.httpService.post(url, data);
      });
    return combineLatest(arrCalls).pipe(
      catchError((error) => {
        this.router.navigate([RoutingEnum.QuotationError], {
          skipLocationChange: true,
          state: {error, step: 'step1'},
        });
        return EMPTY;
      }),
      map((calls) => {
        return calls[calls.length - 1];
      }),
      tap((res) => {
        this.routerDataService.setMotorQuoteObject(res);
      }),
    );
  }

  updatePageForStepTwo() {
    const url = this.apiUrlService.getUrl(Endpoints.UPDATE_PAGE_CONFIGURATOR);
    return this.httpService
      .post(url, {})
      .pipe(tap((res) => this.routerDataService.setMotorQuoteObject(res)));
  }

  updateVehicleForStepTwo() {
    const url = this.apiUrlService.getUrl(Endpoints.UPDATE_PAGE_VEHICLE);
    return this.httpService
      .post(url, {})
      .pipe(tap((res) => this.routerDataService.setMotorQuoteObject(res, false)));
  }

  updatePaperDocumentationForStepThree(mode: string) {
    const url = this.apiUrlService.getUrl(Endpoints.UPDATE_PAGE_DELIVERY);
    return this.httpService
      .post(url, {d099: {mode: mode}})
      .pipe(tap((res) => this.routerDataService.setMotorQuoteObject(res)));
  }

  updateConfiguration(newvalue: string, fieldId: FieldsUuidEnum) {
    const url = this.apiUrlService.getUrl(Endpoints.UPDATE_CONFIGURATION);
    const data = {
      fieldId: fieldId,
      value: newvalue,
    };
    return this.loaderService.handleLoadingChain(this.httpService.post(url, data)).pipe(
      switchMap((res) => {
        if (fieldId === FieldsUuidEnum.PARTNERS) {
          if (res.message === ApiCallErrorMessages.LOWER_DISCOUNT) {
            const dialog = this.dialogService.open(ModalAlertComponent, {
              showCloseIcon: true,
              disableClose: true,
              panelClass: 'no-padding',
              data: {
                uuid: '',
                title: 'modal.warningTitle',
                message: res.messageTxt,
                type: 'warning',
                dismissable: true,
                options: null,
                hasSecondaryAction: false,
              },
            });

            dialog
              .afterClosed()
              .pipe(
                switchMap(() =>
                  this.loaderService.handleLoadingChain(
                    this.httpService.post(url, {fieldId: fieldId, value: '-1'}),
                  ),
                ),
              )
              .subscribe();
            return EMPTY;
          }
          this.analyticsService.sendAnalytics({
            componente: 'personalizza',
            cta: 'convenzione_confermata',
            hit_type: 'event',
            // funnel_type: 'fastquote',
          });
        }
        return of(res);
      }),
      catchError((error) => {
        this.router.navigate([RoutingEnum.QuotationError], {
          skipLocationChange: true,
          state: {error, step: 'step1'},
        });
        return EMPTY;
      }),
      tap((res) => this.routerDataService.setMotorQuoteObject(res)),
    );
  }
  updateData(newvalue: unknown, fieldId: FieldsUuidEnum, fieldSetId: FieldsUuidEnum) {
    const url = this.apiUrlService.getUrl(Endpoints.UPDATE_DATA);
    const data = {
      fieldId: fieldId,
      fieldSetId: fieldSetId,
      value: newvalue,
    };
    return this.httpService.post(url, data).pipe(
      tap((res) => {
        this.routerDataService.setMotorQuoteObject(res);
      }),
      catchError((error) => {
        this.router.navigate([RoutingEnum.QuotationError], {
          skipLocationChange: true,
          state: {error, step: 'step1'},
        });
        return EMPTY;
      }),
    );
  }
  updateContractorData(dataToSend: any): Observable<any> {
    const data = {
      c099: dataToSend,
    };
    const url = this.apiUrlService.getUrl(Endpoints.UPDATE_PAGE_CONTRACTOR);

    return this.httpService
      .post(url, data)
      .pipe(tap((res) => this.routerDataService.setMotorQuoteObject(res)));
  }

  validateAddress(
    addressObj: Record<AddressFormNames, string>,
    type = 'ADDRESS',
  ): Observable<MotorQuoteDTO> {
    const addressData: UpdateAddressDTO = {
      [AddressFormNames.ADDRESS]: {
        uuid: AddressFormUuids.ADDRESS,
        value: addressObj?.[AddressFormNames.ADDRESS],
      },
      [AddressFormNames.HOUSE_NUMBER]: {
        uuid: AddressFormUuids.HOUSE_NUMBER,
        value: addressObj?.[AddressFormNames.HOUSE_NUMBER],
      },
      [AddressFormNames.ZIP_CODE]: {
        uuid: AddressFormUuids.ZIP_CODE,
        value: addressObj?.[AddressFormNames.ZIP_CODE],
      },
      [AddressFormNames.CITY]: {
        uuid: AddressFormUuids.CITY,
        value: addressObj?.[AddressFormNames.CITY].toUpperCase(),
      },
      [AddressFormNames.PROVINCE]: {
        uuid: AddressFormUuids.PROVINCE,
        value: addressObj?.[AddressFormNames.PROVINCE].toUpperCase(),
      },
    };
    const url = this.apiUrlService.getUrl(Endpoints.UPDATE_ADDRESS) + '?operation=' + type;
    return this.httpService
      .post(url, addressData)
      .pipe(tap((res) => this.routerDataService.setMotorQuoteObject(res)));
  }
  validateShippingAddress(
    addressObj: Record<AddressFormNames, string>,
    type = 'DELIVERY_ADDRESS',
  ): Observable<MotorQuoteDTO> {
    const addressData: UpdateAddressDTO = {
      [AddressFormNames.ADDRESS]: {
        uuid: ShippingAddressFormUuids.ADDRESS,
        value: addressObj?.[AddressFormNames.ADDRESS],
      },
      [AddressFormNames.HOUSE_NUMBER]: {
        uuid: ShippingAddressFormUuids.HOUSE_NUMBER,
        value: addressObj?.[AddressFormNames.HOUSE_NUMBER],
      },
      [AddressFormNames.ZIP_CODE]: {
        uuid: ShippingAddressFormUuids.ZIP_CODE,
        value: addressObj?.[AddressFormNames.ZIP_CODE],
      },
      [AddressFormNames.CITY]: {
        uuid: ShippingAddressFormUuids.CITY,
        value: addressObj?.[AddressFormNames.CITY].toUpperCase(),
      },
      [AddressFormNames.PROVINCE]: {
        uuid: ShippingAddressFormUuids.PROVINCE,
        value: addressObj?.[AddressFormNames.PROVINCE].toUpperCase(),
      },
    };
    const url = this.apiUrlService.getUrl(Endpoints.UPDATE_ADDRESS) + '?operation=' + type;
    return this.httpService
      .post(url, addressData)
      .pipe(tap((res) => this.routerDataService.setMotorQuoteObject(res)));
  }

  updateVehicleVariant(newvalue: string) {
    return this.updateData(newvalue, FieldsUuidEnum.VEHICLE_VERSIONS, FieldsUuidEnum.VEHICLE);
  }
  updateTrailerSelection(newvalue: boolean) {
    this.analyticsService.sendAnalytics('updateTrailerSelection ' + newvalue);
    return this.updateData(newvalue, FieldsUuidEnum.TRAILER, FieldsUuidEnum.VEHICLE);
  }
  updatePartner(newvalue: string) {
    return this.updateConfiguration(newvalue, FieldsUuidEnum.PARTNERS);
  }

  updatePaymentPeriod(newvalue: MotorQuoteInputDTO, analyticsFunnel: 'fastquote' | 'allianz_pay') {
    this.updateConfiguration(newvalue.value ?? '', FieldsUuidEnum.PAYMENT).subscribe();
    this.analyticsService.sendAnalytics({
      componente: 'summary',
      cta: this.analyticsService.textForAnalytics(newvalue.label),
      hit_type: 'event',
      // funnel_type: analyticsFunnel,
    });
  }

  sendQuotation(email: string) {
    // Endpoints.SEND
    const url = this.apiUrlService.getUrl(Endpoints.SEND) + '?email=' + email + '&subscribe=true';

    return this.httpService.post(url, {}).pipe(
      tap((res: MotorQuoteDTO) => {
        this.routerDataService.setMotorQuoteObject(res);
        this.analyticsService.sendAnalytics('quotation sent');
      }),
    );
  }

  changePhone(telephone: string) {
    const url = this.apiUrlService.getUrl(Endpoints.CHANGE_PHONE) + '?page=CHANGEPHONE';

    const body = {
      c099: this.splitPhone(telephone),
    };

    return this.httpService.post(url, body).pipe(
      tap((res: MotorQuoteDTO) => {
        this.routerDataService.setMotorQuoteObject(res);
        this.analyticsService.sendAnalytics('Phone changed');
      }),
    );
  }
  getCodeAllianzPay(email: string) {
    // Endpoints.SAVE
    const url = this.apiUrlService.getUrl(Endpoints.SAVE) + '?email=' + email + '&subscribe=true';

    return this.httpService.post(url, {}).pipe(
      tap((res: MotorQuoteDTO) => {
        this.routerDataService.setMotorQuoteObject(res);
        this.analyticsService.sendAnalytics('allianzPay code: ' + res.response.ticket);
      }),
    );
  }

  updatePrivacyAndBuy(privacyOpt: {[key: string]: boolean}) {
    const url = this.apiUrlService.getUrl(Endpoints.BUY_GDPR);

    return this.httpService.post(url, privacyOpt).pipe(
      tap((res: MotorQuoteDTO) => {
        this.routerDataService.setMotorQuoteObject(res);
        //TODO analytics for payment/privacy
        this.analyticsService.sendAnalytics('Privacy setted');
      }),
    );
  }

  buy() {
    const url = this.apiUrlService.getUrl(Endpoints.BUY) + '?subscribe=false';

    return this.httpService.post(url, {}).pipe(
      tap((res: MotorQuoteDTO) => {
        this.routerDataService.setMotorQuoteObject(res);
        //TODO analytics for payment/privacy
        this.analyticsService.sendAnalytics('Buy API done');
      }),
    );
  }

  restoreConfigurationAfterPayment(ticket: string) {
    const url = `${this.apiUrlService.getUrl(Endpoints.RESTORE_CONFIGURATION)}?ticket=${ticket}`;

    return this.httpService.post(url, {}).pipe(
      tap((res: MotorQuoteDTO) => {
        this.routerDataService.setMotorQuoteObject(res);
        //TODO analytics for payment restored
        this.analyticsService.startRecoverConfiguration(res);
        this.analyticsService.sendAnalytics('Payment configuration restored');
      }),
    );
  }

  recoverConfigurationFromCode(allianzPayCode: string) {
    const url = `${this.apiUrlService.getUrl(Endpoints.RECOVER)}/${allianzPayCode}`;

    return this.httpService.get(url).pipe(
      tap((res: MotorQuoteDTO) => {
        this.routerDataService.setMotorQuoteObject(res);
        //TODO analytics for quotation recovered
        this.analyticsService.sendAnalytics('Configuration recovered');
      }),
    );
  }

  addBlueSkyDiscount() {
    const url = this.apiUrlService.getUrl(Endpoints.ADD_BLUESKY_DISCOUNT);

    return this.httpService.get(url).pipe(
      tap((res: MotorQuoteDTO) => {
        this.routerDataService.setMotorQuoteObject(res);
        //TODO analytics for blueSky discount set
        this.analyticsService.sendAnalytics('Added BlueSky discount');
      }),
    );
  }

  removeBlueSkyDiscount() {
    const url = `${this.apiUrlService.getUrl(Endpoints.STATUS_BLUESKY_DISCOUNT)}?discountStatus=N`;

    return this.httpService.post(url, {}).pipe(
      tap((res: MotorQuoteDTO) => {
        this.routerDataService.setMotorQuoteObject(res);
        //TODO analytics for blueSky discount set
        this.analyticsService.sendAnalytics('Removed BlueSky discount');
      }),
    );
  }

  checkPhone(telephoneValue: string) {
    const url = this.apiUrlService.getUrl(Endpoints.CHECK_PHONE);
    const params = {
      data: {
        c099: this.splitPhone(telephoneValue),
      },
    };
    return this.httpService.post(url, params);
  }

  validateOTP(otpValue: string, OTPtoken: string) {
    const url = this.apiUrlService.getUrl(Endpoints.OTP_VALIDATE);
    const params = {
      token: OTPtoken,
      otpCode: otpValue,
    };
    return this.httpService.post(url, params);
  }

  generateOTP(telephoneValue: string) {
    const url = this.apiUrlService.getUrl(Endpoints.OTP_SEND);
    return this.httpService.post(url, {mobileNumber: telephoneValue});
  }

  private splitPhone(phoneNumber: string) {
    const hasInternationalPrefix = phoneNumber.includes('+');
    const internationalPrefix = hasInternationalPrefix
      ? phoneNumber.substring(0, phoneNumber.length - 10)
      : '+39';
    const phoneNumeberWithoutInternationalPrefix = hasInternationalPrefix
      ? phoneNumber.split(internationalPrefix)[1]
      : phoneNumber;

    return {
      telephoneInternationalPrefix: internationalPrefix,
      telephonePrefix: phoneNumeberWithoutInternationalPrefix.substring(0, 3),
      telephone: phoneNumeberWithoutInternationalPrefix.substring(3),
    };
  }

  leadSave(formResponse: HomepageForm, motorData: MotorQuoteDTO) {
    const url = this.apiUrlService.getUrl(Endpoints.LEAD_SAVE);

    const data: LeadSavePayload = {
      email: formResponse.insuredEmail,
      targa: formResponse.vehiclePlate?.toUpperCase(),
      dataNascita: formResponse.insuredBirthday,
      idPrivacy: '0',
      dataScadenzaAnia:
        motorData.response.insuranceData.billing.fieldset[0].fields.expireDatePrvContract.value ??
        '',
      esitoQuotazione: 1,
      quotazione: motorData.response.configuration.currentPrice.total.toString(),
    };

    return this.httpService.post(url, data);
  }

  leadUpdate(phoneNumber: string) {
    const leadId = this.leadService.getSaveId();
    if (!(leadId && phoneNumber && phoneNumber.length > 0)) return EMPTY;
    const url = `${this.apiUrlService.getUrl(Endpoints.LEAD_UPDATE)}?leadId=${leadId}&phoneNumber=${phoneNumber}`;

    return this.httpService.post(url, {}).pipe(
      catchError((e) => {
        console.error('Something went wrong updating lead phone number: ', e);
        return EMPTY;
      }),
    );
  }

  vehicleModalSubmit(data: {[key: string]: unknown}) {
    const url = this.apiUrlService.getUrl(Endpoints.UPDATE_PAGE_VEHICLE_CONFIGURATOR);
    /* const conversionObject = {
      [FieldsUuidEnum.TRAILER]: 'trailerCoupling',
      [FieldsUuidEnum.OPTIONALS]: 'optionals',
      [FieldsUuidEnum.REDUCED_VAT]: 'reducedVat',
      [FieldsUuidEnum.BOX]: 'box',
    }; */
    const body = {
      [FieldsUuidEnum.VEHICLE]: {
        optionals: data[FieldsUuidEnum.OPTIONALS] ?? [],
        trailerCoupling: data[FieldsUuidEnum.TRAILER] ?? false,
        reducedVat: data[FieldsUuidEnum.REDUCED_VAT] ?? false,
        box: data[FieldsUuidEnum.BOX] ?? false,
      },
    };
    return this.httpService
      .post(url, body)
      .pipe(tap((res) => this.routerDataService.setMotorQuoteObject(res)));
  }

  toggleOptional(optionalId: string, operation: 'SELECT' | 'UNSELECT') {
    const url = `${this.apiUrlService.getUrl(Endpoints.SELECT_OPTIONAL)}/${optionalId}?operation=${operation}`;
    return this.httpService.post(url, {}).pipe(
      tap((res: MotorQuoteDTO) => {
        this.routerDataService.setMotorQuoteObject(res);
        //TODO analytics for optional selected/unselected
        this.analyticsService.sendAnalytics(
          `${operation.toLowerCase()} optional with id: ${optionalId}`,
        );
      }),
      catchError((error) => {
        this.router.navigate([RoutingEnum.QuotationError], {
          skipLocationChange: true,
          state: {error, step: 'step1'},
        });
        return EMPTY;
      }),
    );
  }

  isEcommerceActiveForAgency() {
    return this._isEcommerceActiveForAgency;
  }

  getCurrentPlan(motorData?: MotorQuoteDTO) {
    const mtrData = motorData ?? this.routerDataService.getMotorQuoteObject();
    const activePlans = mtrData.response.plans.filter((plan) => plan.active) ?? [];
    return activePlans.length > 0 ? activePlans[0] : undefined;
  }
}
